import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, Sparkles, Truck, Pause, Play, X as XIcon, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMXN } from "@/lib/format";

export default function ArreBoxPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/subscriptions/tiers").then((r) => setTiers(r.data));
  }, []);

  const loadMine = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/subscriptions/mine");
      setMySubs(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    loadMine();
    // eslint-disable-next-line
  }, [user, authLoading]);

  const subscribe = async (tier) => {
    setErr("");
    if (authLoading) return;
    if (!user) {
      navigate("/login?redirect=/arre-box");
      return;
    }
    setSubscribing(tier.id);
    try {
      const { data } = await api.post("/subscriptions/subscribe", {
        tier: tier.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al iniciar la suscripción.");
      setSubscribing(null);
    }
  };

  const action = async (sub_id, action_type) => {
    setErr("");
    try {
      await api.post(`/subscriptions/${sub_id}/action`, { action: action_type });
      await loadMine();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al actualizar suscripción");
    }
  };

  const renew = async (sub_id) => {
    try {
      const { data } = await api.post(`/subscriptions/${sub_id}/renew`);
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al renovar");
    }
  };

  return (
    <div className="bg-arre-black min-h-screen" data-testid="arre-box-page">
      {/* Hero */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden border-b border-arre-border">
        <div className="absolute inset-0 opacity-15 bg-cover bg-center" style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1603048719539-9c9c4d3d04c4?w=1600)"
        }} />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Suscripción</div>
          <h1 className="font-display text-6xl md:text-8xl tracking-wider uppercase text-white leading-[0.85] mb-6">
            ARRE<br /><span className="text-arre-red">BOX</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mb-6">
            Cada mes te llega lo mejor de Sonora a tu puerta, compare. Carne premium, charcutería selecta y los acompañamientos clave. Tú decides cuándo pausar o cancelar.
          </p>
          <div className="flex flex-wrap gap-6 text-xs tracking-[0.2em] uppercase text-white/60">
            <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-arre-red" /> Envío gratis cada mes</span>
            <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-arre-red" /> Cortes exclusivos</span>
            <span className="flex items-center gap-2"><Pause className="w-4 h-4 text-arre-red" /> Pausa cuando quieras</span>
          </div>
        </div>
      </section>

      {/* My subs (if any) */}
      {user && !loading && mySubs.length > 0 && (
        <section className="px-6 md:px-12 py-14 max-w-5xl mx-auto">
          <h2 className="font-display text-3xl tracking-wider uppercase text-white mb-5">Tus boxes</h2>
          <div className="space-y-4" data-testid="my-subs">
            {mySubs.map((s) => (
              <div key={s.id} className="bg-arre-surface border border-arre-border p-6" data-testid={`sub-${s.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs tracking-[0.25em] uppercase text-white/50">{s.tier_name}</div>
                    <div className="font-display text-3xl text-white mt-1">{fmtMXN(s.price)}<span className="text-base text-white/50">/mes</span></div>
                  </div>
                  <div className="flex flex-col items-end">
                    <SubStatusBadge status={s.status} payment={s.payment_status} />
                    {s.next_billing_date && (
                      <div className="text-[11px] text-white/50 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Próximo cargo: {new Date(s.next_billing_date).toLocaleDateString("es-MX")}
                      </div>
                    )}
                  </div>
                </div>
                {s.status !== "cancelled" && (
                  <div className="mt-5 flex flex-wrap gap-2 pt-5 border-t border-arre-border">
                    {s.status === "active" && (
                      <button onClick={() => action(s.id, "pause")} className="btn-arre border border-arre-border hover:border-arre-red text-white text-xs tracking-widest uppercase px-4 py-2 inline-flex items-center gap-2" data-testid={`pause-${s.id}`}>
                        <Pause className="w-3 h-3" /> Pausar
                      </button>
                    )}
                    {s.status === "paused" && (
                      <button onClick={() => action(s.id, "resume")} className="btn-arre bg-arre-red text-white text-xs tracking-widest uppercase px-4 py-2 inline-flex items-center gap-2" data-testid={`resume-${s.id}`}>
                        <Play className="w-3 h-3" /> Reanudar
                      </button>
                    )}
                    {s.payment_status === "paid" && s.status === "active" && (
                      <button onClick={() => renew(s.id)} className="btn-arre border border-arre-border hover:border-arre-red text-white text-xs tracking-widest uppercase px-4 py-2" data-testid={`renew-${s.id}`}>
                        Renovar ahora
                      </button>
                    )}
                    <button onClick={() => action(s.id, "cancel")} className="btn-arre border border-arre-border hover:border-red-500 text-white/70 hover:text-red-500 text-xs tracking-widest uppercase px-4 py-2 inline-flex items-center gap-2 ml-auto" data-testid={`cancel-${s.id}`}>
                      <XIcon className="w-3 h-3" /> Cancelar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tiers */}
      <section className="px-6 md:px-12 py-20 max-w-7xl mx-auto">
        <h2 className="font-display text-4xl md:text-5xl tracking-wider uppercase text-white text-center mb-3">
          Elige tu BOX
        </h2>
        <p className="text-white/60 text-center max-w-2xl mx-auto mb-12">
          Tres niveles, una sola promesa: la mejor carne sonorense en tu mesa cada mes.
        </p>
        {err && <div className="max-w-md mx-auto mb-6 p-3 border border-arre-red bg-arre-red/10 text-sm text-white" data-testid="arrebox-error">{err}</div>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="tiers-grid">
          {tiers.map((t) => (
            <div
              key={t.id}
              className={`relative bg-arre-surface border ${t.popular ? "border-arre-red" : "border-arre-border"} p-7 flex flex-col`}
              data-testid={`tier-${t.id}`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-arre-red text-white text-[10px] tracking-[0.35em] uppercase px-4 py-1 font-bold">
                  Más pedido
                </div>
              )}
              <div className="font-display text-3xl tracking-wider uppercase text-white mb-2">{t.name}</div>
              <p className="text-white/60 text-sm mb-5 min-h-[2.5rem]">{t.tagline}</p>
              <div className="font-display text-5xl text-arre-red mb-1">{fmtMXN(t.price)}</div>
              <div className="text-[11px] tracking-widest uppercase text-white/50 mb-5">/mes · {t.savings_label}</div>
              <ul className="space-y-2.5 mb-7 flex-1">
                {t.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                    <Check className="w-4 h-4 text-arre-red flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => subscribe(t)}
                disabled={subscribing === t.id}
                className={`btn-arre w-full ${t.popular ? "bg-arre-red hover:bg-arre-red-dark" : "bg-white text-arre-black hover:bg-white/90"} text-white font-display text-base tracking-widest uppercase py-3 disabled:opacity-50`}
                data-testid={`subscribe-${t.id}-btn`}
              >
                {subscribing === t.id ? "Procesando..." : "Suscribirme"}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-3xl mx-auto text-center text-xs tracking-[0.2em] uppercase text-white/40">
          Sin compromisos. Pausa o cancela en un click desde tu cuenta.{" "}
          <Link to="/cuenta" className="text-arre-red hover:underline">Mi cuenta</Link>
        </div>
      </section>
    </div>
  );
}

function SubStatusBadge({ status, payment }) {
  if (payment === "pending") return <span className="text-[10px] tracking-[0.2em] uppercase bg-yellow-900/30 border border-yellow-600/50 text-yellow-400 px-2 py-1">Pendiente de pago</span>;
  if (status === "active") return <span className="text-[10px] tracking-[0.2em] uppercase bg-green-900/30 border border-green-600/50 text-green-400 px-2 py-1">Activa</span>;
  if (status === "paused") return <span className="text-[10px] tracking-[0.2em] uppercase bg-blue-900/30 border border-blue-600/50 text-blue-400 px-2 py-1">Pausada</span>;
  if (status === "cancelled") return <span className="text-[10px] tracking-[0.2em] uppercase bg-red-900/30 border border-red-600/50 text-red-400 px-2 py-1">Cancelada</span>;
  return <span className="text-[10px] tracking-[0.2em] uppercase bg-white/10 text-white/70 px-2 py-1">{status}</span>;
}
